import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import WellnessHubPage from "./pages/WellnessHubPage";
import WellnessOverviewPage from "./pages/WellnessOverviewPage";
import BusinessOverviewPage from "./pages/BusinessOverviewPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import DoshaTestPage from "./pages/DoshaTestPage";
import DoshaResultPage from "./pages/DoshaResultPage";
import MensWellnessPage from "./pages/MensWellnessPage";
import WomensWellnessPage from "./pages/WomensWellnessPage";
import FamiliesPage from "./pages/FamiliesPage";
import TeensPage from "./pages/TeensPage";
import RelationshipsPage from "./pages/RelationshipsPage";
import WorkshopsPage from "./pages/WorkshopsPage";
import AICoachPage from "./pages/AICoachPage";
import ShopPage from "./pages/ShopPage";
import ShopSuccessPage from "./pages/ShopSuccessPage";
import ShopQuotePage from "./pages/ShopQuotePage";
import SupportPage from "./pages/SupportPage";
import MarketplacePage from "./pages/MarketplacePage";
import BusinessDetailPage from "./pages/BusinessDetailPage";
import BusinessRegisterPage from "./pages/BusinessRegisterPage";
import MyBusinessPage from "./pages/MyBusinessPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ProfilePage from "./pages/ProfilePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import EcosystemRoutePage from "./pages/EcosystemRoutePage";
import BusinessOffersPage from "./pages/BusinessOffersPage";
import AdminOffersPage from "./pages/AdminOffersPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminContentListPage from "./pages/AdminContentListPage";
import AdminContentEditorPage from "./pages/AdminContentEditorPage";
import AdminContentImportPage from "./pages/AdminContentImportPage";
import AdminKBPage from "./pages/AdminKBPage";
import AdminBusinessesPage from "./pages/AdminBusinessesPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import TermsPage from "./pages/legal/TermsPage";
import CommunityGuidelinesPage from "./pages/legal/CommunityGuidelinesPage";
import DisclaimerPage from "./pages/legal/DisclaimerPage";
import { AuthProvider } from "./lib/auth";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />

            {/* ============================================================ */}
            {/* WELLNESS branch */}
            {/* ============================================================ */}
            <Route path="/wellness" element={<WellnessOverviewPage />} />
            <Route path="/wellness/mens" element={<MensWellnessPage />} />
            <Route path="/wellness/womens" element={<WomensWellnessPage />} />
            <Route path="/wellness/mental" element={<EcosystemRoutePage topicKey="wellness/mental" />} />
            <Route path="/wellness/nutrition" element={<EcosystemRoutePage topicKey="wellness/nutrition" />} />
            <Route path="/wellness/sleep" element={<EcosystemRoutePage topicKey="wellness/sleep" />} />
            <Route path="/wellness/sports" element={<EcosystemRoutePage topicKey="wellness/sports" />} />

            {/* Legacy aliases — keep old URLs working */}
            <Route path="/mens-wellness" element={<MensWellnessPage />} />
            <Route path="/womens-wellness" element={<WomensWellnessPage />} />
            <Route path="/hub" element={<WellnessHubPage />} />
            <Route path="/hub/category/:category" element={<WellnessHubPage />} />

            {/* ============================================================ */}
            {/* RELATIONSHIPS branch */}
            {/* ============================================================ */}
            <Route path="/relationships" element={<RelationshipsPage />} />
            <Route path="/relationships/luv-connect" element={<EcosystemRoutePage topicKey="relationships/luv-connect" />} />
            <Route path="/relationships/couples-connect" element={<EcosystemRoutePage topicKey="relationships/couples-connect" />} />
            <Route path="/relationships/healthy" element={<EcosystemRoutePage topicKey="relationships/healthy" />} />
            <Route path="/relationships/communication" element={<EcosystemRoutePage topicKey="relationships/communication" />} />
            <Route path="/relationships/dating" element={<EcosystemRoutePage topicKey="relationships/dating" />} />
            <Route path="/relationships/wellness" element={<EcosystemRoutePage topicKey="relationships/wellness" />} />

            {/* ============================================================ */}
            {/* FAMILIES & PARENTING branch */}
            {/* ============================================================ */}
            <Route path="/families" element={<FamiliesPage />} />
            <Route path="/families/parenting" element={<EcosystemRoutePage topicKey="families/parenting" />} />
            <Route path="/families/wellness" element={<EcosystemRoutePage topicKey="families/wellness" />} />
            <Route path="/families/teens" element={<TeensPage />} />
            <Route path="/teens" element={<TeensPage />} />

            {/* ============================================================ */}
            {/* BUSINESS & CORPORATE branch */}
            {/* ============================================================ */}
            <Route path="/business" element={<BusinessOverviewPage />} />
            <Route path="/business/partners" element={<EcosystemRoutePage topicKey="business/partners" />} />
            <Route path="/business/corporate" element={<EcosystemRoutePage topicKey="business/corporate" />} />
            <Route path="/business/offers" element={<BusinessOffersPage />} />
            <Route path="/business/directory" element={<EcosystemRoutePage topicKey="business/directory" />} />

            {/* Admin */}
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/offers" element={<AdminOffersPage />} />
            <Route path="/admin/content" element={<AdminContentListPage />} />
            <Route path="/admin/content/new" element={<AdminContentEditorPage />} />
            <Route path="/admin/content/import" element={<AdminContentImportPage />} />
            <Route path="/admin/content/:id/edit" element={<AdminContentEditorPage />} />
            <Route path="/admin/kb" element={<AdminKBPage />} />
            <Route path="/admin/businesses" element={<AdminBusinessesPage />} />

            {/* ============================================================ */}
            {/* CORE ROUTES */}
            {/* ============================================================ */}
            <Route path="/article/:slug" element={<ArticleDetailPage />} />
            <Route path="/dosha" element={<DoshaTestPage />} />
            <Route path="/dosha/result/:resultId" element={<DoshaResultPage />} />
            <Route path="/workshops" element={<WorkshopsPage />} />

            {/* Ask Donny (renamed AI assistant) — legacy /coach redirects */}
            <Route path="/ask-donny" element={<AICoachPage />} />
            <Route path="/coach" element={<Navigate to="/ask-donny" replace />} />

            {/* Shop & marketplace */}
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/success" element={<ShopSuccessPage />} />
            <Route path="/shop/quote" element={<ShopQuotePage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/marketplace/category/:category" element={<MarketplacePage />} />
            <Route path="/marketplace/business/:slug" element={<BusinessDetailPage />} />
            <Route path="/marketplace/register" element={<BusinessRegisterPage />} />
            <Route path="/marketplace/my-business" element={<MyBusinessPage />} />

            {/* Support + auth */}
            <Route path="/support" element={<SupportPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/auth/reset" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* ============================================================ */}
            {/* LEGAL — App Store / Google Play compliance pages             */}
            {/* Public (no auth), MUST resolve before the catch-all below.   */}
            {/* ============================================================ */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />

            <Route path="*" element={<HomePage />} />
          </Routes>
        </Layout>
        <Toaster theme="dark" position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
